import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatDate } from '../lib/api.js';
import { useBranding, StudioMark } from '../lib/branding.jsx';
import { buildRows } from './justify.js';
import Lightbox from './Lightbox.jsx';

/**
 * The public portfolio — "previous work", open to anyone.
 *
 * It reads /api/portfolio, which only ever returns photographs the photographer
 * has doubly opted in (gallery marked public AND photo marked featured), and
 * only at preview resolution. See portfolio.js for the full contract.
 *
 * Downloads are deliberately absent: this is a showcase, not a delivery. The
 * Lightbox is reused with allowDownload off, so a visitor can look closely but
 * has no sanctioned way to take the file.
 */
const GAP = 24;

export default function Portfolio() {
  const { branding } = useBranding();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null); // gallery being viewed
  const [lightbox, setLightbox] = useState(-1);

  useEffect(() => {
    fetch('/api/portfolio')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Could not load the portfolio'))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  // Right-click / drag deterrent, same reasoning as the client gallery: these
  // are still the couples' photographs even when shown publicly.
  useEffect(() => {
    const block = (e) => {
      if (e.target instanceof HTMLImageElement) e.preventDefault();
    };
    document.addEventListener('contextmenu', block);
    document.addEventListener('dragstart', block);
    return () => {
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('dragstart', block);
    };
  }, []);

  if (error) return <div className="centered"><div className="card narrow"><p className="muted">{error}</p></div></div>;
  if (!data) return <div className="centered muted">Loading…</div>;

  if (open) {
    return (
      <PortfolioGallery
        gallery={open}
        onBack={() => {
          setOpen(null);
          setLightbox(-1);
        }}
        lightbox={lightbox}
        setLightbox={setLightbox}
      />
    );
  }

  return (
    <div className="pf">
      <header className="pf-head">
        <StudioMark />
        <span className="label" style={{ marginTop: '1.25rem' }}>Previous Work</span>
        <h1 className="serif pf-title">Weddings</h1>
        {branding?.footer_text && <p className="muted" style={{ maxWidth: '38ch', margin: '0 auto' }}>{branding.footer_text}</p>}
      </header>

      {data.galleries.length === 0 ? (
        <p className="muted" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
          Nothing published yet — check back soon.
        </p>
      ) : (
        <div className="pf-grid">
          {data.galleries.map((g) => (
            <button key={g.id} className="pf-card" onClick={() => setOpen(g)}>
              <span className="pf-thumb">
                <img src={g.cover} alt="" loading="lazy" />
                <span className="pf-veil" />
                <span className="pf-body">
                  <span className="serif pf-name">{g.title}</span>
                  <span className="pf-meta">
                    {g.date ? formatDate(g.date) : ''}
                    {g.date ? ' · ' : ''}
                    {g.photo_count} photograph{g.photo_count === 1 ? '' : 's'}
                  </span>
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {branding?.contact_email && (
        <footer className="gal-footer">
          <span className="label">Enquiries</span>
          <a href={`mailto:${branding.contact_email}`}>{branding.contact_email}</a>
        </footer>
      )}
    </div>
  );
}

/** One wedding's featured photographs, in the same justified grid as the client gallery. */
function PortfolioGallery({ gallery, onBack, lightbox, setLightbox }) {
  const [width, setWidth] = useState(0);
  const gridRef = useRef(null);

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const style = getComputedStyle(el);
    setWidth(el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rows = useMemo(() => buildRows(gallery.photos, width, GAP), [gallery.photos, width]);

  return (
    <div className="pf">
      <header className="pf-head">
        <button className="sm ghost" onClick={onBack} style={{ marginBottom: '1rem' }}>← All weddings</button>
        <span className="label">{gallery.date ? formatDate(gallery.date) : 'Wedding'}</span>
        <h1 className="serif pf-title">{gallery.title}</h1>
      </header>

      <div className="jgrid" ref={gridRef} style={{ '--jgap': `${GAP}px` }}>
        {rows.map((row, ri) => (
          <div className="jrow" key={ri} style={{ height: `${row.height}px` }}>
            {row.items.map(({ photo, index, width: w }) => (
              <button
                key={photo.id}
                className="jcell in"
                style={{ width: `${w}px` }}
                onClick={() => setLightbox(index)}
                aria-label={`Open photograph ${index + 1}`}
              >
                <img src={photo.thumb} alt="" loading="lazy" decoding="async" className="loaded" />
              </button>
            ))}
          </div>
        ))}
      </div>

      {lightbox >= 0 && (
        <Lightbox
          photos={gallery.photos.map((p) => ({ ...p, src: p.full, filename: '' }))}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(-1)}
          allowDownload={false}
        />
      )}
    </div>
  );
}
